import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, Crown, MapPin, Building2, Search, Mail, Phone, 
  Linkedin, MessageSquare, Award, Shield, ChevronRight,
  Globe, User, Star, Scale, Calculator, Home, Target,
  Landmark, GraduationCap, Megaphone, FileText, Cpu,
  UserPlus, ExternalLink, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iconos por área
const AREA_ICONS = {
  legal_inmobiliario: Scale,
  fiscal_inmobiliario: Calculator,
  financiera_inmobiliaria: Target,
  patrimonial: Landmark,
  comercial_industrial: Building2,
  desarrollo_inmobiliario: Home,
  valuacion: Calculator,
  hipotecario: Home,
  infonavit: Building2,
  fovissste: Building2,
  academica: GraduationCap,
  capacitacion: GraduationCap,
  marketing_inmobiliario: Megaphone,
  especializacion_ventas: Target,
  nuevos_negocios: Star,
  notarial: FileText,
  tecnologia_ia: Cpu
};

// Estados de México
const ESTADOS_MEXICO = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Estado de México',
  'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

const DirectorioPublicoPage = () => {
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [directores, setDirectores] = useState([]);
  const [consejoRegional, setConsejoRegional] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEstado, setSelectedEstado] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedEstado, selectedArea]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [areasRes, directorioRes] = await Promise.all([
        axios.get(`${API_URL}/api/institutional/areas`),
        axios.get(`${API_URL}/api/institutional/directorio/liderazgo`, {
          params: {
            estado: selectedEstado || undefined,
            area: selectedArea || undefined
          }
        })
      ]);

      setAreas(areasRes.data.areas || []);
      setDirectores(directorioRes.data.directores || []);
      setConsejoRegional(directorioRes.data.consejo_regional || []);
    } catch (error) {
      console.error('Error loading directory:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDirectores = directores.filter(d => 
    d.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.titulo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredConsejo = consejoRegional.filter(c =>
    c.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cargo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAreaIcon = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    const IconComponent = AREA_ICONS[area?.codigo] || Shield;
    return IconComponent;
  };

  const getBadgeColor = (nivel) => {
    switch (nivel) {
      case 'nacional':
        return 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white';
      case 'regional':
        return 'bg-gradient-to-r from-blue-600 to-blue-500 text-white';
      case 'estatal':
        return 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const DirectorCard = ({ director }) => {
    const AreaIcon = getAreaIcon(director.area_id);
    const area = areas.find(a => a.id === director.area_id);
    
    return (
      <Card className="hover:shadow-lg transition-all duration-300 overflow-hidden" data-testid={`director-card-${director.id}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center overflow-hidden">
                {director.foto ? (
                  <img src={director.foto} alt={director.nombre} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-gray-500" />
                )}
              </div>
              {/* Distintivo de nivel */}
              <div className={`absolute -bottom-2 -right-2 px-2 py-0.5 rounded-full text-xs font-bold ${getBadgeColor(director.nivel)}`}>
                {director.nivel === 'nacional' ? 'NACIONAL' : director.nivel === 'regional' ? 'REGIONAL' : 'ESTATAL'}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-lg">{director.nombre}</h3>
              <p className="text-sm text-gray-600 mb-2">{director.titulo}</p>
              
              {/* Área */}
              {area && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: area.color_primario + '20' }}>
                    <AreaIcon className="w-4 h-4" style={{ color: area.color_primario }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: area.color_primario }}>
                    {area.nombre}
                  </span>
                </div>
              )}

              {/* Estado */}
              {director.estado && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                  <MapPin className="w-4 h-4" />
                  <span>{director.estado}</span>
                </div>
              )}

              {/* Contacto */}
              <div className="flex flex-wrap gap-2">
                {director.whatsapp && (
                  <a
                    href={`https://wa.me/${director.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp
                  </a>
                )}
                {director.email && (
                  <a
                    href={`mailto:${director.email}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                )}
                {director.linkedin && (
                  <a
                    href={director.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-lg text-sm hover:bg-sky-100 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                )}
              </div>
            </div>

            {/* Proveedor Certificado Badge */}
            {director.es_proveedor_certificado && (
              <div className="flex-shrink-0">
                <Badge className="bg-purple-100 text-purple-700">
                  <Award className="w-3 h-3 mr-1" />
                  Proveedor Certificado
                </Badge>
              </div>
            )}
          </div>

          {/* Trayectoria */}
          {director.trayectoria && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600 line-clamp-3">{director.trayectoria}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const ConsejoCard = ({ miembro }) => (
    <Card className="hover:shadow-lg transition-all" data-testid={`consejo-card-${miembro.id}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
            {miembro.foto ? (
              <img src={miembro.foto} alt={miembro.nombre} className="w-full h-full object-cover rounded-xl" />
            ) : (
              <User className="w-7 h-7 text-indigo-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900">{miembro.nombre}</h4>
            <p className="text-sm text-indigo-600 font-medium">{miembro.titulo || miembro.cargo}</p>
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              <MapPin className="w-3 h-3" />
              <span>{miembro.ciudad}, {miembro.estado}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {miembro.whatsapp && (
              <a
                href={`https://wa.me/${miembro.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
              >
                <MessageSquare className="w-4 h-4" />
              </a>
            )}
            {miembro.email && (
              <a
                href={`mailto:${miembro.email}`}
                className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                <Mail className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const VacanteCard = ({ area }) => (
    <Card className="border-2 border-dashed border-amber-300 bg-amber-50/50 hover:border-amber-400 transition-colors">
      <CardContent className="p-6 text-center">
        <div className="w-16 h-16 mx-auto bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
          <UserPlus className="w-8 h-8 text-amber-600" />
        </div>
        <h4 className="font-semibold text-gray-900 mb-1">Vacante - Director {area.nombre}</h4>
        <p className="text-sm text-gray-600 mb-4">
          ¿Quieres representar esta área en UPIM? Buscamos líderes con trayectoria, ética y prestigio profesional.
        </p>
        <Button 
          variant="outline" 
          className="border-amber-400 text-amber-700 hover:bg-amber-100"
          onClick={() => window.location.href = '/postulacion-liderazgo'}
        >
          Postularme
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="directorio-publico-page">
        {/* Hero */}
        <div className="bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-white/10 text-white mb-4">
                <Crown className="w-4 h-4 mr-1" />
                Directorio UPIM
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Directorio de Liderazgo
              </h1>
              <p className="text-lg text-gray-300 mb-8">
                Conoce a los Directores Nacionales, Regionales y al Consejo Regional que lideran UPIM en todo México.
              </p>

              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    data-testid="search-input"
                  />
                </div>
                <select
                  value={selectedEstado}
                  onChange={(e) => setSelectedEstado(e.target.value)}
                  className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  data-testid="estado-filter"
                >
                  <option value="" className="text-gray-900">Todos los estados</option>
                  {ESTADOS_MEXICO.map(estado => (
                    <option key={estado} value={estado} className="text-gray-900">{estado}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <Tabs defaultValue="directores" className="space-y-8">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
                <TabsTrigger value="directores" className="gap-2" data-testid="tab-directores">
                  <Crown className="w-4 h-4" />
                  Directores ({filteredDirectores.length})
                </TabsTrigger>
                <TabsTrigger value="consejo" className="gap-2" data-testid="tab-consejo">
                  <Users className="w-4 h-4" />
                  Consejo Regional ({filteredConsejo.length})
                </TabsTrigger>
              </TabsList>

              {/* Directores Tab */}
              <TabsContent value="directores">
                {/* Filtro por área */}
                <div className="mb-8">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Filtrar por Área:</h3>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedArea === '' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedArea('')}
                    >
                      Todas las áreas
                    </Button>
                    {areas.slice(0, 8).map(area => {
                      const IconComponent = AREA_ICONS[area.codigo] || Shield;
                      return (
                        <Button
                          key={area.id}
                          variant={selectedArea === area.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedArea(area.id)}
                          className="gap-1"
                        >
                          <IconComponent className="w-3 h-3" />
                          {area.nombre_corto || area.nombre}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {filteredDirectores.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {filteredDirectores.map(director => (
                      <DirectorCard key={director.id} director={director} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay directores registrados</h3>
                    <p className="text-gray-600 mb-6">
                      Hay posiciones de liderazgo disponibles. ¿Te gustaría postularte?
                    </p>
                    <Button onClick={() => window.location.href = '/postulacion-liderazgo'}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Postularme como Director
                    </Button>
                  </div>
                )}

                {/* Áreas Vacantes */}
                {areas.filter(a => !a.has_director_nacional).length > 0 && (
                  <div className="mt-12">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-amber-500" />
                      Áreas con Vacantes de Director Nacional
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {areas.filter(a => !a.has_director_nacional).slice(0, 6).map(area => (
                        <VacanteCard key={area.id} area={area} />
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Consejo Regional Tab */}
              <TabsContent value="consejo">
                {filteredConsejo.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredConsejo.map(miembro => (
                      <ConsejoCard key={miembro.id} miembro={miembro} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay miembros del consejo registrados</h3>
                    <p className="text-gray-600">
                      El consejo regional se está formando. Pronto habrá información disponible.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
              ¿Quieres ser parte del liderazgo UPIM?
            </h2>
            <p className="text-indigo-100 mb-6">
              Buscamos profesionales con trayectoria, ética y prestigio para representar a UPIM en sus áreas de especialización.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-indigo-600 hover:bg-indigo-50"
              onClick={() => window.location.href = '/postulacion-liderazgo'}
            >
              <UserPlus className="w-5 h-5 mr-2" />
              Postularme como Líder
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DirectorioPublicoPage;
